import { useEffect, useRef, useState } from "react";
import { Renderer } from "../../../engine/src/renderer.ts";
import { NEUTRAL_LOOK } from "../../../engine/src/fx.ts";
import type { ImageElement, Project, TextElement } from "../../../engine/src/types.ts";
import { t } from "../lib/i18n.ts";
import { removeElement, updateElement } from "../lib/ops.ts";
import { removeClip, splitClip, updateClip } from "../lib/audio.ts";
import type { EditorStore } from "../lib/store.ts";
import { useApp } from "./common.tsx";
import { AudioLibrary, useAudio } from "./audio.tsx";
import { ImageLibrary } from "./images.tsx";
import { useStore } from "./hooks.ts";

const fmtTime = (ms: number): string => { const s = Math.floor(ms / 1000), d = Math.floor((ms % 1000) / 100); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}.${d}`; };

/** The preview used by the simple editor: just the picture, sized by CSS (works at any screen width). */
export function Preview({ store }: { store: EditorStore }) {
  const st = useStore(store), p = st.project!;
  const { api } = useApp();
  const canvas = useRef(null as HTMLCanvasElement | null), rend = useRef(null as Renderer | null);
  const [gpuErr, setGpuErr] = useState(false); const [fontTick, setFontTick] = useState(0);
  useEffect(() => { void document.fonts?.ready.then(() => setFontTick((n: number) => n + 1)); }, []);
  useEffect(() => {
    let ro: ResizeObserver | null = null;
    try {
      const r = new Renderer(canvas.current!, p.width, p.height);
      r.setAssetResolver((id: string) => api.assetUrl(id)); r.onAssetReady = () => setFontTick((n: number) => n + 1);
      const el = canvas.current!;
      ro = new ResizeObserver(() => { const dpr = Math.min(1.5, window.devicePixelRatio || 1); r.setOutputSize(el.clientWidth * dpr, el.clientHeight * dpr); });
      ro.observe(el); const dpr = Math.min(1.5, window.devicePixelRatio || 1); r.setOutputSize(el.clientWidth * dpr || p.width, el.clientHeight * dpr || p.height);
      rend.current = r; setGpuErr(false);
    } catch { setGpuErr(true); }
    return () => { ro?.disconnect(); rend.current = null; };
  }, [p.width, p.height]);
  useEffect(() => {
    const r = rend.current; if (!r) return;
    // draw synchronously: deferring to requestAnimationFrame and cancelling on cleanup starved every frame during playback
    try { r.renderFrame(p, st.timeMs); } catch { /* keep the last frame */ }
  }, [p, st.timeMs, fontTick, gpuErr]);
  useEffect(() => { rend.current?.setPlayback(st.playing, p, st.timeMs); }, [st.playing]);
  const vertical = p.height > p.width, ended = !st.playing && st.timeMs >= p.duration_ms - 1;
  const toggle = () => { if (st.playing) store.pause(); else { if (ended) store.seek(0); store.play(); } };
  return (
    <div className="sp-preview" data-vertical={vertical} id="sp-player">
      <div className="sp-frame" style={{ aspectRatio: `${p.width} / ${p.height}` }} onClick={toggle}>
        <canvas ref={canvas} aria-label="Canvas" />
        {gpuErr && <div className="empty" role="alert">WebGL2 indisponible / unavailable</div>}
        <button className="sp-big" data-on={!st.playing} aria-label={ended ? t("player.replay") : t("simple.play")} tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true">{ended ? <path d="M12 5a7 7 0 11-6.7 9M12 1v4h-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /> : <path d="M8 5.5v13l11-6.5z" fill="currentColor" />}</svg>
        </button>
      </div>
    </div>
  );
}

const Ico = ({ d, fill }: { d: string; fill?: boolean }) => <svg viewBox="0 0 24 24" className="ico" aria-hidden="true" fill={fill ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>;

function Transport({ store, p }: { store: EditorStore; p: Project }) {
  const st = useStore(store), A = useAudio();
  const [loop, setLoop] = useState(false);
  const pct = Math.min(100, (st.timeMs / Math.max(1, p.duration_ms)) * 100);
  useEffect(() => { if (loop && !st.playing && st.timeMs >= p.duration_ms - 1) { store.seek(0); store.play(); } }, [loop, st.playing, st.timeMs]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return;
      const d = e.key === "ArrowLeft" ? -1000 : e.key === "ArrowRight" ? 1000 : 0;
      if (d && !e.ctrlKey && !e.metaKey) { e.preventDefault(); store.seek(Math.max(0, Math.min(p.duration_ms, store.getState().timeMs + d))); }
    };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [p.duration_ms]);
  const fullscreen = () => { const el = document.getElementById("sp-player") as any; if (!el) return; if (document.fullscreenElement) void document.exitFullscreen(); else void (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.()); };
  const marks = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms).slice(1).map((s) => (s.start_ms / Math.max(1, p.duration_ms)) * 100);
  return (
    <div className="sp-transport">
      <button className="btn primary round" onClick={() => (st.playing ? store.pause() : store.play())} aria-label={st.playing ? t("simple.pause") : t("simple.play")}>
        <Ico fill d={st.playing ? "M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" : "M8 5.5v13l11-6.5z"} /></button>
      <div className="sp-track">
        <div className="sp-fill" style={{ width: pct + "%" }} />
        {marks.map((m, i) => <i key={i} className="sp-mark" style={{ left: m + "%" }} />)}
        <input type="range" className="sp-scrub" min={0} max={p.duration_ms} step={10} value={Math.min(st.timeMs, p.duration_ms)}
          aria-label="Timeline" onChange={(e: any) => { store.pause(); store.seek(Number(e.target.value)); }} />
      </div>
      <span className="mono muted sp-time">{fmtTime(st.timeMs)} / {fmtTime(p.duration_ms)}</span>
      <button className="btn sm icon" aria-pressed={loop} onClick={() => setLoop(!loop)} aria-label={t("player.loop")} title={t("player.loop")}><Ico d="M17 2l4 4-4 4M3 11V9a3 3 0 013-3h15M7 22l-4-4 4-4M21 13v2a3 3 0 01-3 3H3" /></button>
      <button className="btn sm icon" aria-pressed={A.muted} onClick={() => A.setMuted(!A.muted)} aria-label={A.muted ? t("au.unmute_preview") : t("au.mute_preview")} title={A.muted ? t("au.unmute_preview") : t("au.mute_preview")}>
        <Ico d={A.muted ? "M11 5L6 9H2v6h4l5 4zM22 9l-6 6M16 9l6 6" : "M11 5L6 9H2v6h4l5 4zM15.5 8.5a5 5 0 010 7M19 5a10 10 0 010 14"} /></button>
      <button className="btn sm icon" onClick={fullscreen} aria-label={t("player.full")} title={t("player.full")}><Ico d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></button>
    </div>
  );
}

function Slider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return <label className="f sp-slider">{label}<input type="range" min={0} max={1} step={0.05} value={value} onChange={(e: any) => onChange(Number(e.target.value))} /></label>;
}

export function SimpleEditor({ store }: { store: EditorStore }) {
  const st = useStore(store), p = st.project!;
  const { api } = useApp();
  const A = useAudio();
  const look = { ...NEUTRAL_LOOK, ...(p.look ?? {}) };
  const setLook = (patch: Partial<typeof look>) => store.edit((q) => ({ ...q, look: { ...NEUTRAL_LOOK, ...(q.look ?? {}), ...patch } }), "look");
  const scenes = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms);
  const activeIdx = scenes.findIndex((s) => st.timeMs >= s.start_ms && st.timeMs < s.start_ms + s.duration_ms);
  return (
    <div className="sp">
      <div className="sp-top">
        <Preview store={store} />
        <Transport store={store} p={p} />
      </div>
      <div className="sp-body">
        <p className="muted sp-hint">{t("simple.hint")}</p>
        <h2 className="sp-h">{t("simple.scenes")}</h2>
        <div className="sp-scenes">
          {scenes.map((sc, i) => {
            const texts = sc.element_ids.map((id) => p.elements[id]).filter((e): e is TextElement => !!e && e.type === "text" && e.visible);
            const pics = sc.element_ids.map((id) => p.elements[id]).filter((e): e is ImageElement => !!e && e.type === "image");
            return (
              <section key={sc.id} className="sp-scene" data-active={i === activeIdx}>
                <button className="sp-scene-head" onClick={() => { store.pause(); store.seek(sc.start_ms + Math.min(1200, sc.duration_ms * 0.4)); }}>
                  <span className="sp-num">{i + 1}</span><span>{t("simple.scene_n", { n: i + 1 })}</span>
                  <span className="muted mono sp-dur">{(sc.duration_ms / 1000).toFixed(1)} s</span>
                </button>
                {texts.length === 0 && pics.length === 0 && <p className="muted" style={{ margin: 0 }}>{t("simple.no_text")}</p>}
                {pics.length > 0 && <div className="sp-pics">{pics.map((el) => (
                  <span key={el.id} className="sp-pic"><img src={api.assetUrl(el.asset_id)} alt={el.name || t("img.title")} loading="lazy" decoding="async" />
                    <button onClick={() => { store.edit((q) => removeElement(q, el.id)); }} aria-label={`${t("img.delete")} ${el.name}`}>×</button></span>))}</div>}
                {texts.map((el) => (
                  <textarea key={el.id} className="textarea sp-text" rows={Math.min(4, Math.max(1, Math.ceil(el.text.length / 28)))} maxLength={280} value={el.text}
                    aria-label={`${t("simple.texts")} ${i + 1}`}
                    onFocus={() => { store.pause(); store.seek(sc.start_ms + Math.min(1500, sc.duration_ms * 0.5)); }}
                    onChange={(e: any) => { const v = e.target.value; if (v.trim()) store.edit((q) => updateElement(q, el.id, { text: v }), `text:${el.id}`); }} />
                ))}
              </section>
            );
          })}
        </div>
        <h2 className="sp-h">{t("img.title")}</h2>
        <ImageLibrary store={store} bare />
        <h2 className="sp-h">{t("au.title")}</h2>
        <AudioLibrary store={store} />
        {p.audio.length > 0 && <div className="stack sp-audio-clips" aria-label={t("au.lane")}>
          {p.audio.map((clip) => {
            const meta = A.meta(clip.asset_id);
            return <div key={clip.id} className="panel sp-audio-clip">
              <button className="btn sm ghost" onClick={() => { store.pause(); store.seek(clip.start_ms); }} title={t("tl.start")}>♪ {clip.name || meta?.name || t("au.clip")}</button>
              <label className="f">{t("au.volume")}: {Math.round(clip.volume * 100)}%
                <input type="range" min={0} max={200} step={1} value={Math.round(clip.volume * 100)}
                  onChange={(e: any) => store.edit((q) => updateClip(q, clip.id, { volume: Number(e.target.value) / 100 }, meta?.duration_ms ?? clip.in_ms + clip.duration_ms), `au:${clip.id}:volume`)} /></label>
              <label className="f"><input type="checkbox" checked={clip.muted} onChange={(e: any) => store.edit((q) => updateClip(q, clip.id, { muted: e.target.checked }, meta?.duration_ms ?? clip.in_ms + clip.duration_ms), `au:${clip.id}:muted`)} /> {t("au.muted")}</label>
              <div className="sp-audio-timing">
                {([["start_ms", t("f.start")], ["duration_ms", t("f.duration")], ["fade_in_ms", t("au.fade_in")], ["fade_out_ms", t("au.fade_out")]] as const).map(([key, label]) =>
                  <label className="f" key={key}>{label}<input className="input" type="number" min={0} step={100} value={clip[key]} onChange={(e: any) => {
                    const value = Number(e.target.value); if (!Number.isFinite(value)) return;
                    store.edit((q) => updateClip(q, clip.id, { [key]: value }, meta?.duration_ms ?? clip.in_ms + clip.duration_ms), `au:${clip.id}:${key}`);
                  }} /></label>)}
              </div>
              <button className="btn sm" onClick={() => {
                const r = splitClip(p, clip.id, st.timeMs);
                if (r) { store.edit(() => r.project); store.seek(st.timeMs); }
              }} title={t("au.split")}>✂</button>
              <button className="btn sm danger" onClick={() => store.edit((q) => removeClip(q, clip.id))} aria-label={`${t("insp.delete")} ${clip.name || meta?.name || t("au.clip")}`}>×</button>
            </div>;
          })}
        </div>}
        <h2 className="sp-h">{t("simple.look")}</h2>
        <div className="sp-look">
          <Slider label={t("simple.look_bloom")} value={look.bloom} onChange={(v) => setLook({ bloom: v })} />
          <Slider label={t("simple.look_vig")} value={look.vignette} onChange={(v) => setLook({ vignette: v })} />
          <Slider label={t("simple.look_grain")} value={look.grain} onChange={(v) => setLook({ grain: v })} />
        </div>
      </div>
    </div>
  );
}
